import Image from 'next/image';
import { Button } from '@/components/buttons';
import Head from 'next/head';
import { Input } from '@/components/inputs';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Link from 'next/link';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

import AuthPagesScaffold from '@/components/auth-pages-scaffold/auth-pages-scaffold.component';
import InlineValidation from '@/components/validations/inline-validation.component';
import StandardToast from '@/components/toast/toast.component';
import EmailVerificationStep, {
  RegAndEmailStatus,
} from '@/components/pages/signup/email-verification-step.component';

import { FIELD_ERROR_MESSAGES } from '@/_shared/constants/errors/form-validation.constants';
import {
  DEFAULT_EMAIL_PATTERN_REGEX,
  ERRORS,
  ERROR_MESSAGES,
} from '@/_shared/constants';
import { ValidationHelper } from '@/_shared/helpers/validation.helper';
import { useSendVerification } from '@/_shared/hooks/function/useEmailService.hooks';
import { signIn } from 'next-auth/react';
import { EmailRequest } from '@/_shared/_api/models/email.model';
import { NextPageWithScaffoldOption } from '@/types/next-page-with-scaffold-option';
import { MaintenanceService } from '@/_shared/services/maintenance.service';
import container from '@/_shared/services/service-container';
import {
  // showErrorToast,
  showWarnToast,
} from '@/_shared/utils/toast.utils';
import {
  getSafeAuthRedirectUrl,
  syncSafeAuthCallbackUrlCookie,
} from '@/_shared/utils/auth-redirect.utils';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required(FIELD_ERROR_MESSAGES['required']('Email address'))
    .matches(
      DEFAULT_EMAIL_PATTERN_REGEX,
      FIELD_ERROR_MESSAGES['email']('Email Address')
    ),
  password: Yup.string().required(FIELD_ERROR_MESSAGES['required']('Password')),
});

const validate = (values: any) => {
  ValidationHelper.validate(values, validationSchema);
};

const Login: NextPageWithScaffoldOption = () => {
  const router = useRouter();

  const maintenanceService =
    container.get<MaintenanceService>('MaintenanceService');

  useEffect(() => {
    const getMaintenanceStatus = async () => {
      const resp = await maintenanceService.getMaintenanceStatus();
      return resp.data;
    };
    getMaintenanceStatus().then((res) => {
      if (res) router.push('/maintenance');
    });
  }, []);

  const [errToast, setErrToast] = useState(false);

  useEffect(() => {
    if (router.query.error) setErrToast(true);
  }, [router.query]);

  useEffect(() => {
    if (!router.isReady) return;

    syncSafeAuthCallbackUrlCookie(router.query.callbackUrl);
  }, [router.isReady, router.query.callbackUrl]);

  const loginFormInitialValue = {
    email: '',
    password: '',
    rememberMe: false,
  };

  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [loginResponse, setLoginResponse] = useState<any>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmailForVerification, setUserEmailForVerification] = useState('');
  const [emailVerificationStatus, setEmailVerificationStatus] =
    useState<RegAndEmailStatus>('initial');

  const { mutateAsync: sendVerificationEmail } = useSendVerification();

  const handleBackToLogin = () => {
    setShowEmailVerification(false);
    setUserEmailForVerification('');
    setEmailVerificationStatus('initial');
    setErrorMessage('');
    setLoginResponse(undefined);
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    const lowerCaseEmail = values.email.toLowerCase();
    const callbackUrl = syncSafeAuthCallbackUrlCookie(router.query.callbackUrl);

    const response = await signIn('credentials', {
      redirect: false,
      email: lowerCaseEmail,
      password: values.password,
      rememberMe: values.rememberMe,
      callbackUrl,
    });

    setIsSubmitting(false);
    setLoginResponse(response);

    if (response && !response.error) {
      // Get redirect url based on callbackUrl query param, if it doesnt exist fallback to /hub
      await router.replace(getSafeAuthRedirectUrl(response.url, callbackUrl));
      return;
    }

    const error = ERROR_MESSAGES[response?.error!];
    setErrorMessage(error);

    if (response?.error === ERRORS.identityEmailNotVerified) {
      setUserEmailForVerification(lowerCaseEmail);
      setShowEmailVerification(true);
      setEmailVerificationStatus('sending');

      try {
        const emailRequest: EmailRequest = {
          recepient: {
            email: lowerCaseEmail,
            name: `there`,
          },
        };
        const result = await sendVerificationEmail(emailRequest);
        if (result) {
          setEmailVerificationStatus('exist-not-verified');
        } else {
          setEmailVerificationStatus('error');
        }
      } catch {
        setEmailVerificationStatus('error');
      }
    }
  };

  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" />
        <title>MeterCube</title>
        <meta property="og:title" content="Login" key="title" />
      </Head>
      <AuthPagesScaffold>
        {showWarnToast(
          errToast,
          router.query.error as string,
          setErrToast,
          'Unauthorized',
          'min-w-[300px]'
        )}
        <div className="grid md:grid-cols-2 w-full h-full md:gap-3 md:min-h-[524px] lg:min-h-[552px] p-3">
          <div className="max-sm:hidden rounded-xl col-span-1 relative">
            <Image
              src="/assets/login_bg.jpg"
              width={500}
              height={500}
              alt="bg_login"
              className="rounded-2xl w-full h-full object-cover"
            />
            <Image
              src="/assets/metercube_logo_light.png"
              width={138}
              height={36}
              alt="metercube_logo"
              className="absolute top-6 left-6 z-10"
            />
          </div>
          <div className="flex justify-center max-sm:items-start max-sm:pt-12 max-sm:p-4 col-span-1 sm:p-10 md:p-4 lg:p-10">
            <div className="w-full flex flex-col justify-center items-center">
              {showEmailVerification ? (
                <EmailVerificationStep
                  email={userEmailForVerification}
                  regStatus={emailVerificationStatus}
                  data={{ firstName: 'there', lastName: '' }}
                  onBackToLogin={handleBackToLogin}
                />
              ) : (
                <>
                  <div className="w-full text-center">
                    <h1 className="text-3xl font-bold tracking-[0.15px] text-title mb-3">
                      Welcome Back
                    </h1>
                    <p className="text-sm font-medium text-gray-500">
                      Please enter your details to proceed with the platform
                    </p>
                  </div>
                  <Formik
                    initialValues={loginFormInitialValue}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validate={validate}
                    validateOnBlur={false}
                    validateOnChange={false}
                  >
                    {(formik) => (
                      <form
                        className="w-full"
                        noValidate
                        onSubmit={formik.handleSubmit}
                      >
                        <div className="w-full my-8">
                          <div className="space-y-[20px] mb-[12px]">
                            <div>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Email"
                                validationError={
                                  (!!formik.touched.email &&
                                    !!formik.errors.email) ||
                                  loginResponse?.error ===
                                    'identity/user-not-found'
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                leadingIconLeft={<Mail />}
                              />
                              {(formik.touched.email && formik.errors.email) ||
                              loginResponse?.error ===
                                'identity/user-not-found' ? (
                                <InlineValidation
                                  errors={
                                    formik.errors.email
                                      ? [formik.errors.email]
                                      : ['User does not exist']
                                  }
                                />
                              ) : null}
                            </div>

                            <div>
                              <Input
                                id="password"
                                name="password"
                                type={passwordVisibility ? 'text' : 'password'}
                                placeholder="Password"
                                validationError={
                                  loginResponse?.error ===
                                    'identity/user-not-found' ||
                                  (formik.touched.password &&
                                    !!formik.errors.password)
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                leadingIconLeft={<Lock />}
                                leadingIconRight={
                                  <div
                                    onClick={() =>
                                      setPasswordVisibility(!passwordVisibility)
                                    }
                                  >
                                    {passwordVisibility ? <EyeOff /> : <Eye />}
                                  </div>
                                }
                              />
                              {formik.touched.password &&
                                formik.errors.password && (
                                  <InlineValidation
                                    errors={[formik.errors.password]}
                                  />
                                )}
                            </div>
                          </div>
                          {!isSubmitting &&
                            [
                              'auth/wrong-password',
                              'identity/user-not-found',
                              'identity/email-not-verified',
                            ].includes(loginResponse?.error) && (
                              <StandardToast
                                header="Login failed"
                                message={
                                  errorMessage ||
                                  ERROR_MESSAGES[ERRORS.authUnexpectedError]
                                }
                                variant="error"
                              />
                            )}
                          <div className="flex items-center justify-between">
                            <div className="flex">
                              {/* <input
                                type="checkbox"
                                className="shrink-0 border-gray-200 rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                                id="hs-default-checkbox"
                              />
                              <label
                                htmlFor="hs-default-checkbox"
                                className="text-sm font-medium text-gray-500 ms-2"
                              >
                                Remember me
                              </label> */}
                            </div>
                            <Link
                              href="/forgot-password"
                              className="text-blue-500 hover:text-blue-600 opacity-90 text-sm font-semibold active:underline"
                            >
                              Forgot password?
                            </Link>
                          </div>
                        </div>
                        <Button
                          type="submit"
                          className="w-full"
                          isLoading={isSubmitting}
                        >
                          Sign In
                        </Button>
                      </form>
                    )}
                  </Formik>
                </>
              )}
            </div>
          </div>
        </div>
      </AuthPagesScaffold>
    </>
  );
};

Login.hideScaffold = true;
export default Login;
